import { Ref } from "vue";

export enum AnchorPosition {
  LeftTop,
  RightTop,
  RightBottom,
  LeftBottom,
}

export const useScale = (
  targetElm: Ref<HTMLElement | undefined>, //オブジェくと
  tempDeg: Ref<number>, //回転した角度
  tempPos: Ref<{ x: number; y: number }>, //中心座標
  tempScale: Ref<number>, //現在の拡大率
  getOriginalSize: () => { width: number; height: number } | undefined,
  scaleObject: (v: number) => void,
  moveObject: (x: number, y: number) => void
) => {
  const radian = Math.PI / 180;
  let targetRadian = 0;
  let rotateCount = 0;
  let state = {
    anchor: AnchorPosition.LeftTop,
    anchorPos: { x: 0, y: 0 },
    startCusorPos: { x: 0, y: 0 }, //マウスダウンした座標
    cursorGap: { x: 0, y: 0 }, //常に00 青いボーダーと画像の隙間
    originalSize: { width: 0, height: 0 },
    startRect: { x: 0, y: 0, width: 0, height: 0 },
    largeRectSize: { width: 0, height: 0 },
    startScale: tempScale.value,
  };

  //クリック時
  const pointerdown = (e: PointerEvent, anchor: AnchorPosition) => {
    //対象の角度が90度を何回超えるか(90度以上で1、180度以上で2)
    rotateCount = calcRotateCount(tempDeg.value);
    //計算で使用する外接四角形の角度Θを求める
    targetRadian = (tempDeg.value - 90 * rotateCount) * radian;

    //90度ごとに1増えている。また、4進数
    anchor = (anchor + rotateCount) % 4;

    const self = e.target as HTMLElement;
    // マウスダウン位置を取得
    const curPos = toRelativePos(e.clientX, e.clientY);
    if (curPos === undefined) return;
    // 今のスケールのサイズを取得
    if (targetElm.value === undefined) return;

    const scaledRect = getScaledRect();
    if (scaledRect === undefined) return;

    // 今のスケールサイズからアンカー（固定座標）位置を取得
    const anchorPos = getAnchorPos(anchor, scaledRect);

    if (anchorPos === undefined) return;
    // スケール率から逆算してオリジナルサイズを覚えておく

    const originalSize = getOriginalSize();
    if (!originalSize) return;
    // クリック位置とオブジェクトの座標のGap計算のためにアンカーの反対側の座標を取得
    const crossAnchor = getCrossAnchor(anchor);
    const crossAnchorPos = getAnchorPos(crossAnchor, scaledRect);
    if (crossAnchorPos === undefined) return;

    //90度毎に幅と高さは逆になる
    let currentObjWidth = 0;
    let currentObjHeight = 0;
    if (rotateCount % 2 == 1) {
      currentObjWidth = originalSize.height * tempScale.value;
      currentObjHeight = originalSize.width * tempScale.value;
    } else {
      currentObjWidth = originalSize.width * tempScale.value;
      currentObjHeight = originalSize.height * tempScale.value;
    }

    //回転したオブジェクトのアンカー位置を取得する。
    let rotatedX: number;
    let rotatedY: number;
    switch (anchor) {
      case AnchorPosition.LeftTop:
        rotatedX = anchorPos.x + currentObjHeight * Math.sin(targetRadian);
        rotatedY = anchorPos.y;
        break;
      case AnchorPosition.RightTop:
        rotatedX = anchorPos.x;
        rotatedY = anchorPos.y + currentObjWidth * Math.sin(targetRadian);
        break;
      case AnchorPosition.RightBottom:
        rotatedX = anchorPos.x - currentObjHeight * Math.sin(targetRadian);
        rotatedY = anchorPos.y;
        break;
      case AnchorPosition.LeftBottom:
        rotatedX = anchorPos.x;
        rotatedY = anchorPos.y - currentObjWidth * Math.sin(targetRadian);
        break;
    }

    // 初期状態として記憶
    state = {
      anchor: anchor,
      anchorPos: anchorPos,
      startCusorPos: {
        x: curPos.x,
        y: curPos.y,
      },
      cursorGap: {
        //クリックした座標と対角アンカーとの差分
        x: Math.abs(crossAnchorPos.x - curPos.x),
        y: Math.abs(crossAnchorPos.y - curPos.y),
      },
      originalSize: originalSize,
      startRect: {
        //今のスケールのサイズ(回転時のx,yは見えていない座標)
        x: rotatedX,
        y: rotatedY,
        width: currentObjWidth,
        height: currentObjHeight,
      },
      largeRectSize: scaledRect,
      startScale: tempScale.value,
    };

    // マウスをキャプチャ
    self.setPointerCapture(e.pointerId);
    self.onpointermove = pointermove;
  };

  //指離したとき
  const pointerup = (e: PointerEvent) => {
    const self = e.target as HTMLElement;
    self.releasePointerCapture(e.pointerId);
    self.onpointermove = null;

    // 移動量を確定
    scaleObject(tempScale.value);
    moveObject(tempPos.value.x, tempPos.value.y);
  };

  //ドラッグ時
  const pointermove = (e: PointerEvent) => {
    // マウスの座標を取得
    const curPos = toRelativePos(e.clientX, e.clientY);
    if (curPos === undefined) return;

    // アンカー位置とマウス座標から新しいサイズを計算
    const fazzySize = {
      width: Math.abs(state.anchorPos.x - curPos.x - state.cursorGap.x),
      height: Math.abs(state.anchorPos.y - curPos.y - state.cursorGap.y),
    };

    // スケールを計算大きい方を採用
    const wScale = fazzySize.width / state.largeRectSize.width;
    const hScale = fazzySize.height / state.largeRectSize.height;
    let scale = Math.max(wScale, hScale);
    scale = scale * state.startScale;
    // 新しいスケール後のサイズを計算
    const scaledSize = {
      width: state.originalSize.width * scale,
      height: state.originalSize.height * scale,
    };

    // anchorに従って新しい左上座標を計算
    const newPos = calcNewPos(state.anchor, state.startRect, scaledSize);

    // 反映(中心座標と拡大縮小後のスケール)
    tempScale.value = scale;
    tempPos.value = newPos;
  };

  const calcRotateCount = (deg: number) => {
    return Math.floor(deg / 90);
  };

  const getAnchorPos = (
    anchor: AnchorPosition,
    rect: { x: number; y: number; width: number; height: number }
  ) => {
    let x = 0;
    let y = 0;

    switch (anchor) {
      case AnchorPosition.LeftTop:
        x = rect.x;
        y = rect.y;
        break;
      case AnchorPosition.RightTop:
        x = rect.x + rect.width;
        y = rect.y;
        break;
      case AnchorPosition.RightBottom:
        x = rect.x + rect.width;
        y = rect.y + rect.height;
        break;
      case AnchorPosition.LeftBottom:
        x = rect.x;
        y = rect.y + rect.height;
        break;
    }
    return {
      x: x,
      y: y,
    };
  };

  //外接四角形の中心座標を返却する
  const calcNewPos = (
    anchor: AnchorPosition,
    anchorPos: { x: number; y: number },
    scaledSize: { width: number; height: number }
  ) => {
    let largeRectangleWidth1 = 0;
    let largeRectangleWidth2 = 0;
    let largeRectangleWidth = 0;
    let largeRectangleHeight1 = 0;
    let largeRectangleHeight2 = 0;
    let largeRectangleHeight = 0;

    if (rotateCount % 2 == 1) {
      largeRectangleWidth1 = scaledSize.height * Math.cos(targetRadian);
      largeRectangleWidth2 = scaledSize.width * Math.sin(targetRadian);
      largeRectangleWidth = largeRectangleWidth1 + largeRectangleWidth2;
      largeRectangleHeight1 = scaledSize.height * Math.sin(targetRadian);
      largeRectangleHeight2 = scaledSize.width * Math.cos(targetRadian);
      largeRectangleHeight = largeRectangleHeight1 + largeRectangleHeight2;
    } else {
      largeRectangleWidth1 = scaledSize.width * Math.cos(targetRadian);
      largeRectangleWidth2 = scaledSize.height * Math.sin(targetRadian);
      largeRectangleWidth = largeRectangleWidth1 + largeRectangleWidth2;
      largeRectangleHeight1 = scaledSize.width * Math.sin(targetRadian);
      largeRectangleHeight2 = scaledSize.height * Math.cos(targetRadian);
      largeRectangleHeight = largeRectangleHeight1 + largeRectangleHeight2;
    }

    const newPos = {
      x: 0,
      y: 0,
    };
    if (anchor == AnchorPosition.RightTop) {
      const tempPos = {
        x: anchorPos.x,
        y: anchorPos.y + largeRectangleHeight2,
      };
      newPos.x = tempPos.x - largeRectangleWidth / 2;
      newPos.y = tempPos.y - largeRectangleHeight / 2;
    }
    if (anchor == AnchorPosition.RightBottom) {
      const tempPos = {
        x: anchorPos.x - largeRectangleWidth1,
        y: anchorPos.y,
      };
      newPos.x = tempPos.x + largeRectangleWidth / 2;
      newPos.y = tempPos.y - largeRectangleHeight / 2;
    }
    if (anchor == AnchorPosition.LeftBottom) {
      const tempPos = {
        x: anchorPos.x,
        y: anchorPos.y + largeRectangleHeight1,
      };
      newPos.x = tempPos.x + largeRectangleWidth / 2;
      newPos.y = tempPos.y - largeRectangleHeight / 2;
    }
    if (anchor == AnchorPosition.LeftTop) {
      const tempPos = {
        x: anchorPos.x - largeRectangleWidth2,
        y: anchorPos.y,
      };
      newPos.x = tempPos.x + largeRectangleWidth / 2;
      newPos.y = tempPos.y + largeRectangleHeight / 2;
    }

    return {
      x: newPos.x,
      y: newPos.y,
    };
  };

  const toRelativePos = (x: number, y: number) => {
    // parentからの相対座標に変換
    const rect = targetElm.value?.parentElement?.getBoundingClientRect();
    if (rect === undefined) return;
    const pos = {
      x: x - (rect?.left ?? 0),
      y: y - (rect?.top ?? 0),
    };
    return pos;
  };

  const getScaledRect = () => {
    const rect = targetElm.value?.getBoundingClientRect(); //拡大後のスケールサイズ
    if (rect === undefined) return;
    const relPos = toRelativePos(rect.x, rect.y);
    if (relPos === undefined) return;
    return {
      x: relPos.x,
      y: relPos.y,
      width: rect.width,
      height: rect.height,
    };
  };

  const getCrossAnchor = (anchor: AnchorPosition) => {
    switch (anchor) {
      case AnchorPosition.LeftTop:
        return AnchorPosition.RightBottom;
      case AnchorPosition.RightTop:
        return AnchorPosition.LeftBottom;
      case AnchorPosition.RightBottom:
        return AnchorPosition.LeftTop;
      case AnchorPosition.LeftBottom:
        return AnchorPosition.RightTop;
    }
  };

  return {
    pointerdown,
    pointerup,
  };
};
